export const DB_NAME = 'irrigonics-lra'
export const DB_VERSION = 1
export const STORES = {
  ASSESSMENTS: 'assessments',
  PENDING_SYNC: 'pending_sync'
}

export class OfflineDB {
  private db: IDBDatabase | null = null

  async init(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains(STORES.ASSESSMENTS)) {
          const store = db.createObjectStore(STORES.ASSESSMENTS, { keyPath: 'id' })
          store.createIndex('survey_date', 'survey_date', { unique: false })
          store.createIndex('status', 'status', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORES.PENDING_SYNC)) {
          db.createObjectStore(STORES.PENDING_SYNC, { keyPath: 'id', autoIncrement: true })
        }
      }
    })
  }

  async saveAssessment(assessment: Record<string, any>): Promise<void> {
    const db = this.db || (await this.init())
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.ASSESSMENTS], 'readwrite')
      const store = tx.objectStore(STORES.ASSESSMENTS)
      const request = store.put(assessment)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.queueForSync(assessment.id, 'assessment_update')
        resolve()
      }
    })
  }

  async getAssessment(id: string): Promise<Record<string, any> | null> {
    const db = this.db || (await this.init())
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.ASSESSMENTS], 'readonly')
      const store = tx.objectStore(STORES.ASSESSMENTS)
      const request = store.get(id)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || null)
    })
  }

  async getAllAssessments(): Promise<Record<string, any>[]> {
    const db = this.db || (await this.init())
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.ASSESSMENTS], 'readonly')
      const store = tx.objectStore(STORES.ASSESSMENTS)
      const request = store.getAll()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || [])
    })
  }

  private async queueForSync(id: string, action: string): Promise<void> {
    const db = this.db || (await this.init())
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.PENDING_SYNC], 'readwrite')
      const store = tx.objectStore(STORES.PENDING_SYNC)
      const request = store.add({ id, action, timestamp: Date.now() })

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async getPendingSync(): Promise<Record<string, any>[]> {
    const db = this.db || (await this.init())
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.PENDING_SYNC], 'readonly')
      const store = tx.objectStore(STORES.PENDING_SYNC)
      const request = store.getAll()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || [])
    })
  }

  async clearPendingSync(): Promise<void> {
    const db = this.db || (await this.init())
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.PENDING_SYNC], 'readwrite')
      const store = tx.objectStore(STORES.PENDING_SYNC)
      const request = store.clear()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }
}

export const offlineDB = new OfflineDB()
